import { trackEvent } from '@host/utils/posthog-track';
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';

/** Where a room move started. `restore` (re-placing the user after a reload) is not reported. */
export type RoomNavigationSource =
  | 'floor_map'
  | 'join_request'
  | 'people_search'
  | 'access_approved'
  | 'cx_hand_raise'
  | 'auditorium'
  | 'go_home'
  | 'restore'
  | 'other';

export const trackRoomNavigated = (props: {
  source: RoomNavigationSource;
  isFloorChange: boolean;
  roomType: string | null;
  floorType: string | null;
  bypassedLock: boolean;
}) => {
  if (props.source === 'restore') return;
  trackEvent('room_navigated', {
    source: props.source,
    is_floor_change: props.isFloorChange,
    room_type: props.roomType,
    floor_type: props.floorType,
    bypassed_lock: props.bypassedLock,
    is_guest: isCurrentUserGuest(),
  });
};

export const trackRoomAccessRequested = (source: RoomNavigationSource) => {
  trackEvent('room_access_requested', { source });
};

export const trackRoomAccessResponded = (
  decision: 'allow' | 'deny',
  requests: { request_timestamp?: string }[]
) => {
  const waits = requests
    .map((r) => (r.request_timestamp ? Date.now() - new Date(r.request_timestamp).getTime() : NaN))
    .filter((ms) => !Number.isNaN(ms));
  trackEvent('room_access_responded', {
    decision,
    request_count: requests.length,
    is_bulk: requests.length > 1,
    // How long the oldest of these visitors waited at the door.
    max_wait_seconds: waits.length ? Math.round(Math.max(...waits) / 1000) : null,
  });
};

export const trackRoomLockToggled = (locked: boolean, overrodeCallLock: boolean) => {
  trackEvent('room_lock_toggled', { locked, overrode_call_lock: overrodeCallLock });
};

export const trackRoomJoinRequestSent = () => trackEvent('room_join_request_sent');

export const trackRoomJoinRequestAnswered = (
  response: 'join' | 'postpone' | 'decline',
  postponeMinutes?: number
) => {
  trackEvent('room_join_request_answered', {
    response,
    postpone_minutes: postponeMinutes ?? null,
  });
};
